// personalization-tokens.ts — SINGLE SOURCE OF TRUTH for campaign/drip message
// personalization tokens, shared by the backend resolver (campaign-variable-
// substitution), the frontend editor (insert buttons), the live preview, and the
// pre-launch validator. Pure TS, no deps, so both repos import it.
//
// A token is written `{Field}` (SINGLE brace) in a message body / poll. At send
// time the backend replaces each `{Field}` with the recipient's value. Tokens are
// matched case- + whitespace-insensitively, so `{First Name}`, `{first_name}` and
// `{FirstName}` all resolve to the same field. NOTE: double braces (`{{x}}`) are
// NOT a token — the matcher is single-brace; `{{x}}` leaves a literal brace and
// won't resolve (this is the bug that shipped "{{first_name}}" verbatim).

/** The canonical fields that exist on every resolved contact (see the backend's
 *  contactDetails object). `key` is the normalized lookup key the resolver reads;
 *  `label` is the human token shown in the editor + inserted as `{label}`;
 *  `variations` are the spellings a user might type, all normalizing to `key`. */
export const CONTACT_TOKEN_FIELDS = [
  { key: 'firstname',    label: 'First Name',    variations: ['First Name', 'FirstName', 'firstname', 'first_name'] },
  { key: 'lastname',     label: 'Last Name',     variations: ['Last Name', 'LastName', 'lastname', 'last_name'] },
  { key: 'displayname',  label: 'Display Name',  variations: ['Display Name', 'DisplayName', 'displayname', 'display_name'] },
  { key: 'phone',        label: 'Phone',         variations: ['Phone', 'phone', 'PhoneNumber', 'phonenumber', 'phone_number', 'Mobile', 'mobile'] },
  { key: 'whatsappname', label: 'WhatsApp Name', variations: ['WhatsApp Name', 'WhatsappName', 'whatsappname', 'whatsapp_name'] },
] as const;

export type ContactTokenKey = typeof CONTACT_TOKEN_FIELDS[number]['key'];
export type ContactTokenLabel = typeof CONTACT_TOKEN_FIELDS[number]['label'];

/** Matches every `{token}` occurrence (single brace; key is anything but `}`). */
export const TOKEN_REGEX = /\{([^}]+)\}/g;

/** Wrap a field label as a token to insert into a message: `{First Name}`. */
export const tokenFor = (label: string): string => `{${label}}`;

/** Case- + whitespace-insensitive normalization used for token matching. */
export const normalizeTokenKey = (key: string): string =>
  key.toLowerCase().replace(/\s+/g, '');

/** keyMappings shape the backend resolver consumes: base key → accepted spellings. */
export const TOKEN_KEY_MAPPINGS: Readonly<Record<ContactTokenKey, readonly string[]>> =
  CONTACT_TOKEN_FIELDS.reduce((acc, f) => {
    acc[f.key] = f.variations;
    return acc;
  }, {} as Record<ContactTokenKey, readonly string[]>);

const NORMALIZED_KNOWN_KEYS = new Set<string>(
  CONTACT_TOKEN_FIELDS.flatMap((f) => [normalizeTokenKey(f.key), ...f.variations.map(normalizeTokenKey)]),
);

/** True if `rawKey` (the text inside `{…}`) resolves to a canonical contact field,
 *  OR matches one of `extraColumnTitles` (the selected audience's custom columns). */
export const isKnownToken = (rawKey: string, extraColumnTitles: readonly string[] = []): boolean => {
  const n = normalizeTokenKey(rawKey);
  return NORMALIZED_KNOWN_KEYS.has(n) || extraColumnTitles.some((t) => normalizeTokenKey(t) === n);
};

/** Every distinct unknown token in `text` — tokens that won't resolve at send and
 *  would ship literally. `extraColumnTitles` are the chosen audience's columns, so
 *  custom-column tokens (e.g. `{Company}`) validate dynamically. Empty array = OK. */
export const findUnknownTokens = (
  text: string | undefined,
  extraColumnTitles: readonly string[] = [],
): string[] => {
  if (!text) return [];
  const unknown = new Set<string>();
  for (const m of text.matchAll(TOKEN_REGEX)) {
    const raw = m[1].trim();
    if (!isKnownToken(raw, extraColumnTitles)) unknown.add(raw);
  }
  return [...unknown];
};
